import type { PreloadedEntryPoint } from 'react-relay';
import type { Route, RouterContext } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	defaultRegistry,
	createHook,
	type StoreActionApi,
	type BoundActions,
	type HookFunction,
	type StoreInstance,
} from '@atlassian/react-sweet-state';

type LastLoadedRoute = {
	route: Route;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: null | PreloadedEntryPoint<any>;
};

type PrefetchingRoute = {
	routerContext: RouterContext;
	expiresAt: number;
	preloadStartedAt: number | null;
	preloadPromise: Promise<unknown> | null;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: null | PreloadedEntryPoint<any>;
};

export type State = {
	lastLoadedRoute: null | LastLoadedRoute;
	prefetchingRoute: null | PrefetchingRoute;
};

export const setLastLoadedRoute =
	({ route, entryPointReference }: LastLoadedRoute) =>
	({ setState }: StoreActionApi<State>): void => {
		setState({
			lastLoadedRoute: {
				route,
				entryPointReference,
			},
		});
	};

export const setPrefetchingRoute =
	({
		routerContext,
		entryPointReference,
		expiresAt,
		preloadStartedAt,
		preloadPromise,
	}: PrefetchingRoute) =>
	({ setState }: StoreActionApi<State>): void => {
		setState({
			prefetchingRoute: {
				routerContext,
				expiresAt,
				preloadStartedAt,
				preloadPromise,
				entryPointReference,
			},
		});
	};

const actions = {
	setLastLoadedRoute,
	setPrefetchingRoute,
};

type Actions = typeof actions;

const EntryPointsStore = createStore<State, Actions>({
	initialState: { lastLoadedRoute: null, prefetchingRoute: null },
	actions,
	name: 'react-resource-router/entry-points',
});

export const useLastLoadedRoute: HookFunction<
	LastLoadedRoute | null,
	BoundActions<
		State,
		{
			setLastLoadedRoute: ({
				route,
				entryPointReference,
			}: LastLoadedRoute) => ({ setState }: StoreActionApi<State>) => void;
			setPrefetchingRoute: ({
				routerContext,
				entryPointReference,
				expiresAt,
				preloadStartedAt,
				preloadPromise,
			}: PrefetchingRoute) => ({ setState }: StoreActionApi<State>) => void;
		}
	>,
	void
> = createHook<State, Actions, LastLoadedRoute | null>(EntryPointsStore, {
	selector: (state) => state.lastLoadedRoute,
});

export const getEntryPointsStore = (): StoreInstance<
	State,
	{
		setLastLoadedRoute: ({
			route,
			entryPointReference,
		}: LastLoadedRoute) => ({ setState }: StoreActionApi<State>) => void;
		setPrefetchingRoute: ({
			routerContext,
			entryPointReference,
			expiresAt,
			preloadStartedAt,
			preloadPromise,
		}: PrefetchingRoute) => ({ setState }: StoreActionApi<State>) => void;
	}
> => defaultRegistry.getStore<State, Actions>(EntryPointsStore);

export const getEntryPointsState = (): Readonly<State> =>
	getEntryPointsStore().storeState.getState();

export const getLastLoadedRoute = (): LastLoadedRoute | null =>
	getEntryPointsState().lastLoadedRoute;
export const getPrefetchingRoute = (): PrefetchingRoute | null =>
	getEntryPointsState().prefetchingRoute;
